/**
 * 出来事から実況文を作る（P1 の簡易版）。テンプレート・優先度・間隔の制御は P3（docs/06_ui-ux.md 7 章）。
 */
import type { SimEvent } from '../sim/events';
import { regionLabel } from '../sim/weather/forecast';

export type Speaker = 'system' | 'announcer' | 'commentator' | 'rival' | 'viewer';

export interface CommentaryLine {
  speaker: Speaker;
  text: string;
  /** rival の発言者、または話題の選手 */
  athleteId?: string;
}

const LEVEL_JA = { low: '低い離陸地', ridge: '稜線', peak: '山頂' } as const;

const RIVAL_LINES: Record<string, string[]> = {
  peakHunt: ['山頂から勝負だ', 'てっぺんまで登るぞ', '高さは正義だ'],
  ridgeLaunch: ['稜線から出る', 'このくらいの高さで十分だ'],
  lowLaunch: ['さっさと飛んで谷を越える', 'まずは短く飛ぶ'],
  hikeOnly: ['今日は脚で稼ぐ', '歩いたほうが確実だ'],
  avoidStorm: ['あの雲は嫌な感じだ。歩こう', '雷雨は勘弁だ'],
};

export function describeEvent(
  e: SimEvent,
  nameOf: (id: string) => string,
  isPlayer: (id: string) => boolean,
  pick: (n: number) => number,
): CommentaryLine | null {
  const who = 'athleteId' in e ? nameOf(e.athleteId) : '';
  const you = 'athleteId' in e && isPlayer(e.athleteId);
  const sama = you ? 'あなた' : `${who}選手`;
  switch (e.type) {
    case 'raceStart':
      return { speaker: 'announcer', text: 'レーススタート！ 6 人の選手がアルプスへ飛び出しました！' };
    case 'dayStart':
      return e.dayIndex > 0
        ? {
            speaker: 'announcer',
            text: `Day${e.dayIndex + 1} の朝です。各選手、ビバーク地点から再スタート！`,
          }
        : null;
    case 'takeoff':
      return {
        speaker: 'announcer',
        text: `${sama}、${LEVEL_JA[e.level]}から離陸！`,
        athleteId: e.athleteId,
      };
    case 'landed':
      if (e.planned)
        return {
          speaker: 'announcer',
          text: `${sama}、予定どおり着陸。${(e.distanceM / 1000).toFixed(1)}km のフライト`,
          athleteId: e.athleteId,
        };
      return {
        speaker: 'announcer',
        text: `${sama}、予定外の着陸…… ${(e.distanceM / 1000).toFixed(1)}km で降りました`,
        athleteId: e.athleteId,
      };
    case 'crashed':
      return {
        speaker: 'announcer',
        text: `${sama}、墜落！ 大きなケガはなさそうですが、スタミナを大きく失いました`,
        athleteId: e.athleteId,
      };
    case 'treeLanding':
      return {
        speaker: 'commentator',
        text: `${sama}は木に引っかかりましたね。回収に時間がかかりそうです`,
        athleteId: e.athleteId,
      };
    case 'checkpointPassed':
      return { speaker: 'announcer', text: `${sama}、CP${e.cp} 通過！`, athleteId: e.athleteId };
    case 'goalReached':
      return { speaker: 'announcer', text: `${sama}、ゴール！ ${e.rank} 位！`, athleteId: e.athleteId };
    case 'leadChange':
      return { speaker: 'announcer', text: `首位交代！ ${sama}がトップに立ちました`, athleteId: e.athleteId };
    case 'overtake':
      return you
        ? {
            speaker: 'announcer',
            text: `あなたが${nameOf(e.passedId)}選手を抜きました！`,
            athleteId: e.athleteId,
          }
        : { speaker: 'announcer', text: `${who}選手があなたを抜きました`, athleteId: e.athleteId };
    case 'windWaitStarted':
      return {
        speaker: you ? 'commentator' : 'announcer',
        text: `${sama}、${e.reason === 'tailwind' ? '追い風' : '強風'}で風待ち（${e.windKmh}km/h）`,
        athleteId: e.athleteId,
      };
    case 'takeoffAborted':
      if (e.reason !== 'wind') return null;
      return {
        speaker: 'announcer',
        text:
          e.walkToNodeId !== null
            ? `${sama}、風がおさまらず離陸を断念。走って斜面を下ります`
            : `${sama}、風がおさまらず離陸を断念`,
        athleteId: e.athleteId,
      };
    case 'takeoffFailed':
      return {
        speaker: 'commentator',
        text:
          e.reason === 'tailwind'
            ? `${sama}、追い風の離陸で翼がつぶれた！ 数分でやり直します`
            : `${sama}、離陸をやり直しています。風がきついですね`,
        athleteId: e.athleteId,
      };
    case 'thermalEntered':
      return you
        ? { speaker: 'commentator', text: `いいサーマルをつかみましたね（${e.climbMs.toFixed(1)}m/s）` }
        : null;
    case 'bigClimb':
      return { speaker: 'announcer', text: `${sama}、サーマルで ${e.gainM}m 上昇！`, athleteId: e.athleteId };
    case 'stall':
      return you
        ? {
            speaker: 'commentator',
            text: e.deep ? '突風で翼がつぶれた！ 大きく高度を失っています' : '突風で失速、高度を落としました',
          }
        : null;
    case 'staminaLow':
      return you
        ? { speaker: 'commentator', text: 'スタミナが 30 を切りました。脚が重くなってきます' }
        : null;
    case 'cuDeveloping':
      return {
        speaker: 'commentator',
        text: `${regionLabel(e.x, e.z)}の雲が高く育ってきました。雷雨の前触れかもしれません`,
      };
    case 'stormFormed':
      return { speaker: 'announcer', text: `${regionLabel(e.x, e.z)}で雷雨が発生！` };
    case 'stormDissipated':
      return { speaker: 'announcer', text: `${regionLabel(e.x, e.z)}の雷雨はおさまってきました` };
    case 'sunset':
      return { speaker: 'announcer', text: '日没です。飛行中の選手は着陸に入ります' };
    case 'dayEnd':
      return { speaker: 'announcer', text: `Day${e.dayIndex + 1} 終了。各選手ビバークに入ります` };
    case 'raceEnd':
      return { speaker: 'announcer', text: 'レース終了！ お疲れさまでした！' };
    case 'forcedPause':
      return { speaker: 'system', text: '予定外の着陸。ルートを組み直してください' };
    case 'cpuDecision': {
      const lines = RIVAL_LINES[e.reason];
      if (!lines || (e.reason === 'hikeOnly' && pick(3) !== 0)) return null;
      return { speaker: 'rival', text: lines[pick(lines.length)] as string, athleteId: e.athleteId };
    }
    default:
      return null;
  }
}
